import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Inbox, Loader2 } from 'lucide-react';
import type { PrivacyRequest } from 'shared';
import {
  useClosePrivacyRequest,
  usePrivacyRequests,
  type PrivacyRequestFilter,
} from '@/hooks/usePrivacyRequests';
import { ConfirmDialog, ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import {
  PRIVACY_REQUEST_HOW,
  PRIVACY_REQUEST_LABEL,
  deadlineTone,
  formatDate,
  subjectPath,
  type DeadlineTone,
} from '@/lib/privacyRequestView';

const FILTERS: { value: PrivacyRequestFilter; label: string }[] = [
  { value: 'abertos', label: 'Por tratar' },
  { value: 'tratados', label: 'Tratados' },
];

const DEADLINE_STYLE: Record<DeadlineTone, string> = {
  late: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  soon: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
  ok: 'text-muted-foreground',
};

function Deadline({ request }: { request: PrivacyRequest }) {
  const tone = deadlineTone(request, new Date());
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium tabular-nums ${DEADLINE_STYLE[tone]}`}>
      {tone === 'late' ? 'Atrasado · ' : ''}
      {formatDate(request.dueAt)}
    </span>
  );
}

/**
 * Requests Diggers and Encarregados made from the PWA about their own data. The
 * RGPD gives a month to answer; the oldest come first.
 */
export default function PedidosPrivacidadePage() {
  const { toasts, toast, dismiss } = useToast();
  const [filter, setFilter] = useState<PrivacyRequestFilter>('abertos');
  const [closing, setClosing] = useState<PrivacyRequest | null>(null);
  const { data, isLoading, isError } = usePrivacyRequests(filter);
  const close = useClosePrivacyRequest();
  const requests = data?.requests ?? [];
  const open = filter === 'abertos';

  const confirmClose = async () => {
    if (!closing) return;
    try {
      await close.mutateAsync(closing.id);
      toast.success(`Pedido de ${closing.subjectName} marcado como tratado.`);
    } catch {
      toast.error('Não foi possível marcar o pedido como tratado.');
    }
    setClosing(null);
  };

  return (
    <>
      <Helmet>
        <title>Pedidos de privacidade | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Inbox className="w-6 h-6 text-primary" />
            Pedidos de privacidade
          </h1>
          <p className="text-muted-foreground mt-1">
            O que os Diggers e os Encarregados pedem na app sobre os seus dados. Tens um mês para
            responder a cada um.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          {(['copy', 'erasure'] as const).map((kind) => (
            <div key={kind} className="card px-4 py-3">
              <p className="font-medium text-foreground">{PRIVACY_REQUEST_LABEL[kind]}</p>
              <p className="text-muted-foreground mt-0.5">{PRIVACY_REQUEST_HOW[kind]}</p>
            </div>
          ))}
        </div>

        <div
          className="flex items-center gap-1 p-1 rounded-md border border-border bg-muted/30 w-fit"
          role="group"
          aria-label="Filtrar pedidos"
        >
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                filter === f.value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="card overflow-hidden">
          {isLoading && <LoadingBlock />}
          {isError && <ErrorBlock message="Não foi possível carregar os pedidos." />}
          {!isLoading && !isError && requests.length === 0 && (
            <p className="py-16 text-center text-sm text-muted-foreground">
              {open ? 'Não há pedidos por tratar.' : 'Ainda não foi tratado nenhum pedido.'}
            </p>
          )}

          {!isLoading && !isError && requests.length > 0 && (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" aria-label="Pedidos de privacidade">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Pessoa', 'Pedido', 'Mensagem', 'Recebido', open ? 'Prazo' : 'Tratado', ''].map((h, i) => (
                    <th key={h || i} scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-accent/30 transition-colors align-top">
                    <td className="px-4 py-3">
                      <Link to={subjectPath(r)} className="font-medium text-foreground hover:text-primary hover:underline">
                        {r.subjectName}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        {r.participantId !== null ? `Digger n.º ${r.participantId}` : 'Encarregado de Educação'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-foreground">{PRIVACY_REQUEST_LABEL[r.kind]}</td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs">
                      {r.message ?? <span className="text-muted-foreground/40">—</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground tabular-nums">{formatDate(r.createdAt)}</td>
                    <td className="px-4 py-3">
                      {open ? (
                        <Deadline request={r} />
                      ) : (
                        <span className="text-muted-foreground tabular-nums">
                          {r.handledAt ? formatDate(r.handledAt) : ''}
                          {r.handledByName ? ` · ${r.handledByName}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {open && (
                        <button
                          type="button"
                          onClick={() => setClosing(r)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-medium text-foreground hover:bg-accent"
                        >
                          {close.isPending && close.variables === r.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Marcar como tratado
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {closing && (
        <ConfirmDialog
          title="Marcar como tratado"
          confirmLabel="Marcar como tratado"
          tone="primary"
          isPending={close.isPending}
          onConfirm={confirmClose}
          onClose={() => setClosing(null)}
        >
          {closing.kind === 'copy'
            ? `Já enviaste a ${closing.subjectName} o ficheiro com os seus dados?`
            : `Falaste com ${closing.subjectName} e decidiram não eliminar a conta? Se a eliminares, o pedido fica tratado sozinho.`}
        </ConfirmDialog>
      )}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
